import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Dictionary } from 'lodash';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ErrorResponse } from 'src/app/core/models/common.models';
import { CustomField, CustomFieldOption } from 'src/app/core/models/custom-fields.models';
import { CustomFieldsBackendService } from 'src/app/core/services/backend/custom-fields-backend.service';
import { CoreState } from 'src/app/core/store/reducers';
import {
  getCompanyFieldsListSelector,
  getFieldsListSelector,
  getFieldsMapSelector,
  getFormFieldsListSelector,
} from 'src/app/core/store/reducers/custom-fields';
import {
  CompanyCustomFieldsReqListAction,
  CompanyCustomFieldsReqListErrorAction,
  CompanyCustomFieldsReqListSuccessAction,
  CustomFieldsDeleteAction,
  CustomFieldsDeleteErrorAction,
  CustomFieldsDeleteSuccessAction,
  CustomFieldsReqAddAction,
  CustomFieldsReqAddErrorAction,
  CustomFieldsReqAddSuccessAction,
  CustomFieldsUpdateAction,
  CustomFieldsUpdateErrorAction,
  CustomFieldsUpdateSuccessAction,
} from '../actions/custom-fields';

@Injectable({ providedIn: 'root' })
export class CustomFieldsStoreService {
  public readonly map$ = this.store$.pipe(select(getFieldsMapSelector));
  public readonly list$ = this.store$.pipe(select(getFieldsListSelector));
  public readonly optionsMap$: Observable<Dictionary<CustomFieldOption>>;

  public readonly formList$ = this.store$.pipe(select(getFormFieldsListSelector));
  public readonly companyList$ = this.store$.pipe(select(getCompanyFieldsListSelector));

  constructor(
    private store$: Store<CoreState>, //
    private be: CustomFieldsBackendService,
  ) {
    this.optionsMap$ = this.list$.pipe(
      map((fieldsMap) => {
        return fieldsMap.reduce<Dictionary<CustomFieldOption>>((acc, field) => {
          if (Array.isArray(field.options)) {
            field.options.forEach((option) => {
              acc[option.guid] = option;
            });
          }
          return acc;
        }, {});
      }),
    );
  }

  public loadFormFields(): Observable<readonly CustomField[]> {
    this.store$.dispatch(new CompanyCustomFieldsReqListAction());

    return this.be.formList().pipe(
      map((res) => {
        this.store$.dispatch(new CompanyCustomFieldsReqListSuccessAction(res));
        return res;
      }),
      catchError((err: ErrorResponse) => {
        this.store$.dispatch(new CompanyCustomFieldsReqListErrorAction(err));
        return throwError(() => err);
      }),
    );
  }

  public loadCompanyFields(): Observable<readonly CustomField[]> {
    this.store$.dispatch(new CompanyCustomFieldsReqListAction());

    return this.be.companyList().pipe(
      map((res) => {
        this.store$.dispatch(new CompanyCustomFieldsReqListSuccessAction(res));
        return res;
      }),
      catchError((err: ErrorResponse) => {
        this.store$.dispatch(new CompanyCustomFieldsReqListErrorAction(err));
        return throwError(() => err);
      }),
    );
  }

  public create(field: CustomField): Observable<CustomField> {
    this.store$.dispatch(new CustomFieldsReqAddAction(field));

    return this.be.add(field).pipe(
      map((res) => {
        this.store$.dispatch(new CustomFieldsReqAddSuccessAction(res));
        return res;
      }),
      catchError((err: ErrorResponse) => {
        this.store$.dispatch(new CustomFieldsReqAddErrorAction(err));
        return throwError(() => err);
      }),
    );
  }

  public update(field: CustomField): Observable<CustomField> {
    this.store$.dispatch(new CustomFieldsUpdateAction(field));

    return this.be.update(field).pipe(
      map((res) => {
        this.store$.dispatch(new CustomFieldsUpdateSuccessAction(res));
        return res;
      }),
      catchError((err: ErrorResponse) => {
        this.store$.dispatch(new CustomFieldsUpdateErrorAction(err));
        return throwError(() => err);
      }),
    );
  }

  public delete(field: CustomField): Observable<void> {
    this.store$.dispatch(new CustomFieldsDeleteAction(field.id));

    return this.be.delete(field).pipe(
      map(() => {
        this.store$.dispatch(new CustomFieldsDeleteSuccessAction(field.id));
      }),
      catchError((err: ErrorResponse) => {
        this.store$.dispatch(new CustomFieldsDeleteErrorAction(err));
        return throwError(() => err);
      }),
    );
  }

  public save(field: CustomField): Observable<CustomField> {
    return field.id ? this.update(field) : this.create(field);
  }
}
